'use client';
import { useState } from 'react';
import { useResource } from '@/lib/use-resource';

interface EarningsRow {
  symbol: string;
  state: 'upcoming' | 'reported';
  date: string;
  daysOut: number;
  epsEstimate: number | null;
  revenueEstimate: number | null;
  epsActual: number | null;
  revenueActual: number | null;
  epsSurprisePct: number | null;
  revenueSurprisePct: number | null;
}

interface EarningsFile {
  asOf: string;
  bySymbol: Record<string, EarningsRow>;
  calendar: EarningsRow[];
}

interface EnrichmentRow {
  lastQ: {
    period: string;
    epsEstimate: number | null;
    epsActual: number | null;
    surprisePct: number | null;
    announcementDate: string;
    priorYrEps: number | null;
    yoyGrowthPct: number | null;
  } | null;
  fy1: { current: number | null; priorMonth: number | null; changePct: number | null; direction: 'up' | 'down' | 'flat' | null };
}
interface EnrichmentFile { bySymbol: Record<string, EnrichmentRow> }

function daysLabel(r: EarningsRow): string {
  const d = r.daysOut;
  if (r.state === 'reported') {
    if (d === 0) return 'reported today';
    if (d === -1) return 'reported yesterday';
    return `reported ${-d}d ago`;
  }
  if (d === 0) return 'TODAY';
  if (d === 1) return 'tomorrow';
  if (d < 7) return `${d}d out`;
  if (d < 14) return '1w out';
  if (d < 30) return `${Math.floor(d / 7)}w out`;
  return `${Math.floor(d / 30)}mo out`;
}

function urgencyColor(r: EarningsRow): string {
  if (r.state === 'reported') return '#94a3b8';
  if (r.daysOut <= 0) return '#22c55e';
  if (r.daysOut <= 1) return '#fcd34d';
  if (r.daysOut <= 7) return '#fb923c';
  if (r.daysOut <= 14) return '#f59e0b';
  return '#94a3b8';
}

function fmtEps(v: number | null): string {
  if (v == null) return '—';
  return `$${v.toFixed(2)}`;
}

function fmtRev(v: number | null): string {
  if (v == null) return '—';
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  return `$${(v / 1e6).toFixed(0)}M`;
}

function fmtSurprise(v: number | null): { text: string; tone: string } {
  if (v == null) return { text: '—', tone: 'text-[#64748b]' };
  const pct = v * 100;
  const sign = v >= 0 ? '+' : '';
  const tone = pct >= 5 ? 'text-emerald-400'
    : pct >= 0 ? 'text-emerald-300'
    : pct >= -5 ? 'text-orange-400'
    : 'text-red-400';
  return { text: `${sign}${pct.toFixed(1)}%`, tone };
}

/** Tiny FY1 revision-trend cell — arrow + signed % change over the prior ~30 days. */
function Fy1Trend({ fy1 }: { fy1: { current: number | null; priorMonth: number | null; changePct: number | null; direction: 'up' | 'down' | 'flat' | null } | null }) {
  if (!fy1 || fy1.changePct == null || fy1.direction == null) {
    return <div className="text-right tabular-nums text-[#64748b]">—</div>;
  }
  const tone = fy1.direction === 'up' ? 'text-emerald-400'
    : fy1.direction === 'down' ? 'text-red-400'
    : 'text-[#94a3b8]';
  const arrow = fy1.direction === 'up' ? '↑'
    : fy1.direction === 'down' ? '↓'
    : '→';
  const pct = fy1.changePct * 100;
  const sign = pct >= 0 ? '+' : '';
  return (
    <div className={`text-right tabular-nums text-[10px] ${tone}`} title={fy1.priorMonth != null ? `FY1 EPS: ${fy1.priorMonth.toFixed(2)} → ${(fy1.current ?? 0).toFixed(2)}` : ''}>
      {arrow} {sign}{pct.toFixed(1)}%
    </div>
  );
}

export function UpcomingEarnings({ dataUrl = '/api/upcoming-earnings' }: { dataUrl?: string } = {}) {
  const { data } = useResource<EarningsFile>(dataUrl);
  const { data: enrichment } = useResource<EnrichmentFile>('/api/earnings-enrichment');
  const [openSymbol, setOpenSymbol] = useState<string | null>(null);

  if (!data || !data.calendar?.length) return null;

  return (
    <section className="mb-6">
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-[13px] font-semibold text-white tracking-wide uppercase">Earnings — current quarter</h2>
        <div className="text-[10px] text-[#64748b]">
          {data.calendar.length} portfolio names · upcoming + recently reported · click a row for detail
        </div>
      </div>
      <div className="bg-[#0d1117] border border-[#1e293b] rounded-lg overflow-hidden">
        <div className="grid grid-cols-[72px_84px_132px_1fr_1fr_0.8fr_1fr_1fr_0.8fr_0.9fr_1fr_1fr] gap-2 px-3 py-2 text-[9px] uppercase tracking-wider text-[#64748b] border-b border-[#1e293b]">
          <div>Symbol</div>
          <div>Date</div>
          <div className="text-amber-300">Status</div>
          <div className="text-right" title="EPS estimate for the upcoming quarter">EPS Est</div>
          <div className="text-right" title="EPS actual for the upcoming quarter (once reported)">EPS Actual</div>
          <div className="text-right" title="Surprise % on the upcoming quarter (actual vs estimate, 1 decimal)">Surprise</div>
          <div className="text-right" title="EPS estimate for the most recently reported quarter">LQ Est</div>
          <div className="text-right" title="EPS actual for the most recently reported quarter">LQ Act</div>
          <div className="text-right" title="Surprise % on the prior quarter (1 decimal, green = beat, red = miss)">LQ Surp</div>
          <div className="text-right" title="Year-over-year EPS growth on the most recently reported quarter (LQ vs same-quarter prior year)">Y/Y %</div>
          <div className="text-right" title="FY1 EPS estimate (current fiscal year consensus)">FY1 Est</div>
          <div className="text-right" title="FY1 EPS revision over the last ~30 days (consensus moving up or down)">FY1 Δ30d</div>
        </div>
        {data.calendar.map((r) => {
          const isOpen = openSymbol === r.symbol;
          const isReported = r.state === 'reported';
          const surprise = fmtSurprise(r.epsSurprisePct);
          const enr = enrichment?.bySymbol?.[r.symbol] ?? null;
          const lq = enr?.lastQ ?? null;
          const fy1 = enr?.fy1 ?? null;
          const lqSurprise = fmtSurprise(lq?.surprisePct ?? null);
          const yoy = fmtSurprise(lq?.yoyGrowthPct ?? null);
          return (
            <div key={r.symbol} className="border-t border-[#1e293b]/40">
              <button
                type="button"
                onClick={() => setOpenSymbol(isOpen ? null : r.symbol)}
                className={`w-full grid grid-cols-[72px_84px_132px_1fr_1fr_0.8fr_1fr_1fr_0.8fr_0.9fr_1fr_1fr] gap-2 px-3 py-0.5 text-[12px] leading-tight items-center hover:bg-[#1e293b]/30 text-left ${
                  r.daysOut <= 1 && r.state === 'upcoming' ? 'bg-amber-500/5' : ''
                } ${isReported ? 'opacity-90' : ''}`}
                aria-expanded={isOpen}
              >
                <div className="font-mono font-bold text-white flex items-baseline gap-1.5">
                  <span className="text-amber-400/70 select-none">{isOpen ? '▾' : '▸'}</span>
                  {r.symbol}
                </div>
                <div className="text-[10px] text-[#94a3b8] tabular-nums">{r.date}</div>
                <div
                  className="text-[11px] tabular-nums font-semibold"
                  style={{ color: urgencyColor(r) }}
                >
                  {daysLabel(r)}
                </div>
                <div className="text-right tabular-nums text-[#cbd5e1]">{fmtEps(r.epsEstimate)}</div>
                <div className="text-right tabular-nums">
                  {isReported && r.epsActual != null ? (
                    <span className="text-white font-semibold">{fmtEps(r.epsActual)}</span>
                  ) : (
                    <span className="text-[#64748b]">—</span>
                  )}
                </div>
                <div className={`text-right tabular-nums ${isReported ? surprise.tone : 'text-[#64748b]'}`}>
                  {isReported ? surprise.text : '—'}
                </div>
                <div className="text-right tabular-nums text-[10px] text-[#94a3b8]" title={lq ? `Reported ${lq.announcementDate} for ${lq.period}` : 'No prior-quarter data'}>
                  {fmtEps(lq?.epsEstimate ?? null)}
                </div>
                <div className="text-right tabular-nums text-[10px] text-[#cbd5e1]" title={lq ? `${lq.period} actual EPS` : 'No prior-quarter data'}>
                  {fmtEps(lq?.epsActual ?? null)}
                </div>
                <div className={`text-right tabular-nums text-[10px] ${lq?.epsActual != null ? lqSurprise.tone : 'text-[#64748b]'}`} title={lq?.epsActual != null ? `${lq.period} surprise vs estimate` : 'No prior-quarter data'}>
                  {lq?.epsActual != null ? lqSurprise.text : '—'}
                </div>
                <div className={`text-right tabular-nums text-[10px] ${lq?.yoyGrowthPct != null ? yoy.tone : 'text-[#64748b]'}`} title={lq?.priorYrEps != null ? `Y/Y EPS growth: ${lq.period} vs same Q prior year ($${lq.priorYrEps.toFixed(2)})` : 'No prior-year quarterly comp'}>
                  {lq?.yoyGrowthPct != null ? yoy.text : '—'}
                </div>
                <div className="text-right tabular-nums text-[#cbd5e1]">{fmtEps(fy1?.current ?? null)}</div>
                <Fy1Trend fy1={fy1} />
              </button>

              {isOpen && (
                <ReleaseDetail row={r} />
              )}
            </div>
          );
        })}
      </div>
      <div className="text-[10px] text-[#64748b] mt-2 leading-relaxed">
        Source: FMP earnings calendar; actuals confirmed against the BBG SAPI real-time earnings detector. A reported row stays in this panel until the next quarter&apos;s date is scheduled. Click any row to see the full release breakdown.
      </div>
    </section>
  );
}

interface ReleaseEvent { ts: string; tier: string; type: string; symbol: string; headline: string }

function ReleaseDetail({ row }: { row: EarningsRow }) {
  // Pull the BBG/FMP captured earnings event headlines for this symbol so the
  // expansion shows the actual press-release headline alongside the numbers.
  const { data: raw } = useResource<{ events: ReleaseEvent[] }>(`/api/research-events?symbol=${row.symbol}&days=120`);
  const events: ReleaseEvent[] | null = raw
    ? (raw.events || []).filter((e) => e.type === 'earnings' || e.type === 'reported' || /earn|EPS|revenue/i.test(e.headline))
    : null;

  const surprise = fmtSurprise(row.epsSurprisePct);
  const revSurprise = fmtSurprise(row.revenueSurprisePct);

  return (
    <div className="px-5 py-4 bg-[#0a0e14] border-t border-[#1e293b]/50 text-[12px] leading-relaxed space-y-3">
      <div className="grid md:grid-cols-2 gap-x-6 gap-y-2">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-[#64748b] mb-0.5">EPS</div>
          <div className="flex items-baseline gap-3 text-[13px]">
            <span className="text-[#94a3b8]">est <span className="text-[#cbd5e1] tabular-nums">{fmtEps(row.epsEstimate)}</span></span>
            {row.state === 'reported' && (
              <>
                <span className="text-[#94a3b8]">actual <span className="text-white font-semibold tabular-nums">{fmtEps(row.epsActual)}</span></span>
                <span className={`tabular-nums font-semibold ${surprise.tone}`}>{surprise.text}</span>
              </>
            )}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-[#64748b] mb-0.5">Revenue</div>
          <div className="flex items-baseline gap-3 text-[13px]">
            <span className="text-[#94a3b8]">est <span className="text-[#cbd5e1] tabular-nums">{fmtRev(row.revenueEstimate)}</span></span>
            {row.state === 'reported' && (
              <>
                <span className="text-[#94a3b8]">actual <span className="text-white font-semibold tabular-nums">{fmtRev(row.revenueActual)}</span></span>
                <span className={`tabular-nums font-semibold ${revSurprise.tone}`}>{revSurprise.text}</span>
              </>
            )}
          </div>
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-wide text-[#64748b] mb-1.5">
          Release headlines{events == null ? ' (loading…)' : ` (${events.length})`}
        </div>
        {events == null ? null : events.length === 0 ? (
          <div className="text-[11px] text-[#64748b] italic">
            {row.state === 'upcoming'
              ? 'No release captured yet — the desk will append the headline once BBG/FMP indexes the print.'
              : 'No headlines captured for this report. The numbers above came from the FMP calendar; the BBG SAPI detector did not log a press release for this symbol.'}
          </div>
        ) : (
          <ul className="space-y-1 text-[11.5px]">
            {events.map((e, i) => (
              <li key={i} className="flex gap-3">
                <span className="text-[#64748b] tabular-nums whitespace-nowrap">{e.ts.slice(0, 10)}</span>
                <span className="uppercase tracking-wide text-[10px] font-semibold text-amber-400 whitespace-nowrap">
                  {e.tier}
                </span>
                <span className="text-[#cbd5e1]">{e.headline}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
